/**
 * BCE Accordion Module
 * 
 * Transforms TYPO3 content elements into interactive Bootstrap-style accordions.
 * Supports hash-based deep linking and automatic container structure creation.
 * 
 * Features:
 * - Automatic DOM structure transformation
 * - Hash-based deep linking with smooth scrolling
 * - Bootstrap accordion compatibility
 * - Container structure auto-creation
 * - TYPO3 wrapper cleanup
 * - Instance management for multiple accordions
 * - Scroll-to-section on hash navigation
 * 
 * @class BceAccordion
 */
class BceAccordion {
    constructor() {
        this.instances = new WeakMap();
        this.autoInit = false;
        // Track initial hash handling across per-element create() calls
        this._initialHash = null;
        this._initialHashHandled = false;
        this._initialHashOpenScheduled = false;
        this.setupHashListener();
        this.setupHeightRecalcListener();
    }

    /**
     * Initialize all accordion elements found in the DOM
     * Automatically called by BCE system on page load
     */
    init() {
        // Capture initial hash once at init() time
        this._initialHash = this.getHashToOpen();
        const accordionContainers = document.querySelectorAll('.bce_accordion');
        
        if (!accordionContainers.length) {
            return;
        }

        accordionContainers.forEach((container, index) => {
            if (!this.instances.has(container)) {
                this.create(container, index);
            }
        });

        // Handle initial hash after all items are created
        this.scheduleInitialHashOpen();
    }

    /**
     * Get hash for accordion navigation
     * 
     * @returns {string|null} Hash to open or null
     */
    getHashToOpen() {
        const hash = window.location.hash;
        if (hash && hash.startsWith("#aci_")) {
            return hash.substring(1);
        }
        return null;
    }

    /**
     * Create an accordion instance for a container
     * 
     * @param {HTMLElement} container - The container element with bce_accordion class
     * @param {number} index - Index of the accordion
     * @returns {Object|null} Accordion instance or null
     */
    create(container, index) {
        if (this.instances.has(container)) {
            return this.instances.get(container);
        }

        // Ensure container structure exists
        const containerEl = this.ensureContainerStructure(container, index);
        if (!containerEl) {
            return null;
        }

        // Transform container classes
        container.classList.remove('bce_accordion');
        container.classList.add('accordion');

        // Set accordion ID
        if (!container.id) {
            container.id = `accordion-${index}`;
        }
        const accordionId = container.id;

        // Transform content to accordion items
        const accordionItems = this.transformContentToAccordion(container, accordionId);

        // Clean up TYPO3 wrapper classes
        this.cleanupTypo3Wrappers(container);

        // Create instance
        const instance = {
            container,
            accordionId,
            items: accordionItems,
            destroy: () => {
                this.destroyInstance(container);
            }
        };

        // Store instance
        this.instances.set(container, instance);

        // Ensure initial hash navigation also works when BCE calls create() per element
        // (BCE prefers create() over init()). We schedule it only once across all calls.
        this.scheduleInitialHashOpen();

        return instance;
    }

    /**
     * Ensure proper container structure exists
     * 
     * @param {HTMLElement} accordion - The accordion element
     * @param {number} index - Index for logging
     * @returns {HTMLElement|null} Container element or null
     */
    ensureContainerStructure(accordion, index) {
        let container = accordion.querySelector(':scope > .container');
        
        if (!container) {
            container = document.createElement('div');
            container.className = 'container';
            
            const row = accordion.querySelector(':scope > .row');
            if (row) {
                container.appendChild(row);
                accordion.insertBefore(container, accordion.firstChild);
            } else {
                console.warn(`Accordion ${index}: No .row found, skipping transformation`);
                return null;
            }
        }

        return container;
    }

    /**
     * Transform content elements into accordion items
     * 
     * @param {HTMLElement} accordion - The accordion container
     * @param {string} accordionId - ID of the accordion
     * @returns {Array} Array of created accordion items
     */
    transformContentToAccordion(accordion, accordionId) {
        const children = Array.from(accordion.querySelectorAll(':scope .container > .row > .col-12 > *'));
        const accordionItems = [];

        for (let j = 0; j < children.length - 1; j++) {
            const header = children[j];
            const content = children[j + 1];
            const headline = header.querySelector('h1,h2,h3,h4,h5,h6');

            if (!headline || !content) continue;

            const headingId = `${accordionId}-heading-${j}`;
            const collapseId = `${accordionId}-${1000 + j}`;

            // Extract content class with aci_ prefix
            const contentClass = Array.from(content.classList).find(cls => cls.startsWith('aci_'));

            // Create accordion item structure
            const accordionItem = this.createAccordionItem({
                headline,
                content,
                headingId,
                collapseId,
                accordionId,
                contentClass
            });

            // Replace original elements
            header.replaceWith(accordionItem);
            content.remove();
            
            accordionItems.push({
                element: accordionItem,
                collapseId,
                contentClass
            });

            j++; // Skip next item as it was the content
        }

        return accordionItems;
    }

    /**
     * Create accordion item structure
     * 
     * @param {Object} params - Parameters for accordion item creation
     * @returns {HTMLElement} Created accordion item
     */
    createAccordionItem(params) {
        const { headline, content, headingId, collapseId, accordionId, contentClass } = params;

        // Create button
        const button = document.createElement('button');
        button.className = 'accordion-button collapsed';
        button.type = 'button';
        button.setAttribute('data-bs-toggle', 'collapse');
        button.setAttribute('data-bs-target', `#${collapseId}`);
        button.setAttribute('aria-expanded', 'false');
        button.setAttribute('aria-controls', collapseId);
        button.setAttribute('data-bs-parent', `#${accordionId}`);
        
        if (contentClass) {
            button.classList.add(contentClass);
        }
        
        button.innerHTML = headline.innerHTML;

        // Add accordion icon
        const icon = document.createElement('span');
        icon.className = 'accordion-icon';
        button.appendChild(icon);

        // Create header
        const h4 = document.createElement('h4');
        h4.className = 'accordion-header';
        h4.id = headingId;
        h4.appendChild(button);

        // Create body
        const body = document.createElement('div');
        body.className = 'accordion-body';
        while (content.firstChild) {
            body.appendChild(content.firstChild);
        }

        // Create collapse container
        const collapse = document.createElement('div');
        collapse.className = 'accordion-collapse collapse';
        collapse.id = collapseId;
        collapse.setAttribute('aria-labelledby', headingId);
        collapse.setAttribute('data-bs-parent', `#${accordionId}`);
        collapse.appendChild(body);
        
        if (contentClass) {
            collapse.classList.add(contentClass);
        }

        // Create accordion item wrapper
        const item = document.createElement('div');
        item.className = 'accordion-item';
        item.appendChild(h4);
        item.appendChild(collapse);

        return item;
    }

    /**
     * Schedule opening the accordion item referenced by the initial URL hash
     * after DOM transformation completes. Ensures it runs only once.
     */
    scheduleInitialHashOpen() {
        // Cache initial hash if not yet captured
        if (this._initialHash === null) {
            this._initialHash = this.getHashToOpen();
        }
        if (!this._initialHash || this._initialHashHandled || this._initialHashOpenScheduled) {
            return;
        }
        this._initialHashOpenScheduled = true;
        // Defer to the next frame so all create() calls finish first
        requestAnimationFrame(() => {
            this.openAccordionByHash(this._initialHash);
            // Small fallback retry to handle late DOM mutations without downsides
            setTimeout(() => {
                if (!this.isHashOpen(this._initialHash)) {
                    this.openAccordionByHash(this._initialHash);
                }
            }, 120);
            this._initialHashHandled = true;
            this._initialHashOpenScheduled = false;
        });
    }

    /**
     * Check if the hash-referenced accordion is currently open
     * @param {string} hashToOpen
     * @returns {boolean}
     */
    isHashOpen(hashToOpen) {
        const collapseEl = document.querySelector(`.accordion-collapse[class~="${hashToOpen}"]`);
        const buttonEl = document.querySelector(`.accordion-button[class~="${hashToOpen}"]`);
        return !!(collapseEl && buttonEl && collapseEl.classList.contains('show'));
    }

    /**
     * Clean up TYPO3 wrapper classes
     * 
     * @param {HTMLElement} accordion - The accordion container
     */
    cleanupTypo3Wrappers(accordion) {
        accordion.querySelectorAll('.frame.frame-type-textpic.frame-layout-default').forEach(wrapper => {
            wrapper.classList.remove('frame', 'frame-type-textpic', 'frame-layout-default');
            wrapper.style.marginBottom = '0';
        });
    }

    /**
     * Open accordion by hash
     * 
     * @param {string} hashToOpen - Hash class to open
     */
    openAccordionByHash(hashToOpen) {
        // Use attribute selector to match exact class token (handles special chars like '+')
        const collapseEl = document.querySelector(`.accordion-collapse[class~="${hashToOpen}"]`);
        const buttonEl = document.querySelector(`.accordion-button[class~="${hashToOpen}"]`);

        if (collapseEl && buttonEl) {
            // Close other open items within the same accordion container
            const parentSelector = collapseEl.getAttribute('data-bs-parent');
            let parent = null;
            if (parentSelector) {
                parent = document.querySelector(parentSelector);
            }
            if (!parent) {
                parent = collapseEl.closest('.accordion');
            }
            if (parent) {
                parent.querySelectorAll('.accordion-collapse.show').forEach(openEl => {
                    if (openEl !== collapseEl) {
                        openEl.classList.remove('show');
                        const openId = openEl.id;
                        const openBtn = parent.querySelector(`[data-bs-target="#${openId}"]`);
                        if (openBtn) {
                            openBtn.classList.add('collapsed');
                            openBtn.setAttribute('aria-expanded', 'false');
                        }
                    }
                });
            }

            // Open target if not open yet
            if (!collapseEl.classList.contains('show')) {
                collapseEl.classList.add('show');
                buttonEl.classList.remove('collapsed');
                buttonEl.setAttribute('aria-expanded', 'true');
            }

            // Opening the panel manually does not fire Bootstrap's shown.bs.collapse,
            // so trigger the equal-height recalculation ourselves (see triggerHeightRecalc).
            this.triggerHeightRecalc();

            // Always scroll to the header, even if already open
            setTimeout(() => {
                const headerHeight = this.getCssVarPx('--header-height', 100);
                const headerEl = buttonEl.closest('.accordion-header') || buttonEl;
                try {
                    // Prefer native scroll-margin-top for precise alignment with sticky headers
                    headerEl.style.scrollMarginTop = `${headerHeight}px`;
                    headerEl.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
                } catch (e) {
                    // Fallback for older browsers
                    const y = headerEl.getBoundingClientRect().top + window.pageYOffset - headerHeight;
                    window.scrollTo({ top: y, behavior: 'smooth' });
                }
            }, 200);
        } else {
            console.warn(`Accordion: Hash ${hashToOpen} not found`);
        }
    }

    /**
     * Read a CSS pixel variable from :root/body and return a number in px
     * @param {string} varName CSS variable name (e.g., '--header-height')
     * @param {number} fallbackPx Fallback value in px if not set or invalid
     * @returns {number}
     */
    getCssVarPx(varName, fallbackPx = 0) {
        try {
            const root = getComputedStyle(document.documentElement).getPropertyValue(varName).trim();
            const body = getComputedStyle(document.body).getPropertyValue(varName).trim();
            const val = root || body || '';
            const n = parseFloat(val);
            return Number.isFinite(n) ? n : fallbackPx;
        } catch (e) {
            return fallbackPx;
        }
    }

    /**
     * Destroy an accordion instance
     * 
     * @param {HTMLElement} container - The accordion container
     */
    destroyInstance(container) {
        const instance = this.instances.get(container);
        if (!instance) return;

        // Reset accordion to original state if needed
        // This is a complex operation and might need specific implementation
        // based on requirements

        // Remove from instances map
        this.instances.delete(container);
    }


    /**
     * Recalculate equal-heights when an accordion panel becomes visible.
     *
     * The site theme (main.js) runs an equal-height routine over
     * `.frame-layout-redLine` / `.frame-type-teaser` boxes: it measures their
     * offsetHeight and locks every box to the tallest value via an inline
     * `height: …px`. When those boxes sit inside a collapsed accordion panel at
     * page load, they measure 0 → `height: 0px`, and the text stays clipped even
     * after the panel is opened.
     *
     * That routine is bound to `window`'s `resize` event and resets the boxes to
     * `height: auto` before re-measuring, so dispatching a synthetic `resize`
     * once a panel is fully shown makes it re-measure with real, visible heights.
     */
    setupHeightRecalcListener() {
        // Bootstrap collapse events bubble to the document (fires on user opens).
        document.addEventListener('shown.bs.collapse', (e) => {
            const target = e.target;
            if (target && target.classList && target.classList.contains('accordion-collapse')) {
                this.triggerHeightRecalc();
            }
        });
        // Panels open at page load never fire shown.bs.collapse — run once after the
        // theme's initial equal-height pass has locked heights.
        if (typeof window !== 'undefined') {
            window.addEventListener('load', () => {
                setTimeout(() => this.triggerHeightRecalc(), 200);
            });
        }
    }

    /**
     * Undo the height:0 that the theme's equal-height routine locks onto
     * redLine/teaser frames when it measures them inside a collapsed panel, then
     * nudge that routine to re-equalize with the now-visible heights.
     */
    triggerHeightRecalc() {
        if (typeof window === 'undefined') return;
        requestAnimationFrame(() => {
            // Self-contained fix: clear the locked inline height so the frames show
            // at their natural height regardless of whether the theme re-measures.
            document.querySelectorAll('.accordion-collapse.show .frame[style*="height"]').forEach(el => {
                el.style.height = '';
            });
            // Then let the theme re-equalize (make them equal) if it can.
            window.dispatchEvent(new Event('resize'));
        });
    }

    /**
     * Setup hash change listener for deep linking
     */
    setupHashListener() {
        window.addEventListener('hashchange', () => {
            const hashToOpen = this.getHashToOpen();
            if (hashToOpen) {
                this.openAccordionByHash(hashToOpen);
            }
        });
    }

    /**
     * Get instance for external access
     * 
     * @param {HTMLElement} container - The accordion container
     * @returns {Object|null} Accordion instance or null
     */
    getInstance(container) {
        return this.instances.get(container);
    }

    /**
     * Open specific accordion item by ID
     * 
     * @param {string} collapseId - ID of the collapse element to open
     */
    openItem(collapseId) {
        const collapseEl = document.getElementById(collapseId);
        const buttonEl = document.querySelector(`[data-bs-target="#${collapseId}"]`);

        if (collapseEl && buttonEl) {
            collapseEl.classList.add('show');
            buttonEl.classList.remove('collapsed');
            buttonEl.setAttribute('aria-expanded', 'true');
        }
    }

    /**
     * Close specific accordion item by ID
     * 
     * @param {string} collapseId - ID of the collapse element to close
     */
    closeItem(collapseId) {
        const collapseEl = document.getElementById(collapseId);
        const buttonEl = document.querySelector(`[data-bs-target="#${collapseId}"]`);

        if (collapseEl && buttonEl) {
            collapseEl.classList.remove('show');
            buttonEl.classList.add('collapsed');
            buttonEl.setAttribute('aria-expanded', 'false');
        }
    }
}

export default new BceAccordion();
